import type { PokerGameTile } from '@ps/poker-games-tile-mappers';
import type { ReactElement } from 'react';

import type {
  ClubRecentTournPlayerResult,
  ClubRecentTournamentSummary,
  HgClubInfo,
  HgGetClubOptionsReply,
  PlayerListElement,
  RecentTournament,
  TableStaticData
} from '@swf/generated/types/api/v0/SwHomeGames';
import type {
  ClubSeason,
  ClubSeasonUserStats,
  HgScheduledTournament
} from '@swf/generated/types/api/v1-preview/SwHomeGames';

import type { RoutingKey } from '../enums';

export const PokerModalRoutes = {
  createClub: '/homegames/clubs/create-a-club',
  createClubSuccess: '/homegames/clubs/create-a-club-success',
  joinClub: '/homegames/clubs/join-a-club',
  createCashGame: '/homegames/create-game/cash-game',
  createCashGameSuccess: '/homegames/create-game/cash-game/success',
  createTournament: '/homegames/create-game/tournament',
  createTournamentSuccess: '/homegames/create-game/tournament/success',
  deleteGamePrompt: '/homegames/prompt/delete-game',
  membersAdminActions: '/homegames/members/admin-actions',
  notification: '/homegames/notification'
};

export type HGPokerGameTile = PokerGameTile & {
  supported?: boolean;
  name?: string;
  maxPlayers?: string | number;
};

export interface GamesByClubs {
  [clubId: string]: HGPokerGameTile[];
}

export interface Loading {
  loaderName: string;
  isLoading: boolean;
}

export interface Games {
  recentTourns?: RecentTournament[];
  registeredTourns: HgScheduledTournament[];
  selectedGameIds: string[];
  tables?: TableStaticData[];
  tourns?: RecentTournament[];
}

export type ClubSeasonStats = Omit<ClubSeasonUserStats, 'rank' | 'knockouts' | 'points'> & {
  rank?: number | string;
  knockouts?: number | string | ReactElement;
  points?: number | string | ReactElement;
};

export interface UILayout {
  clubNavHidden: boolean;
  standingsPlayMoneyFlag: boolean;
}

export interface HomeGamesError {
  critical: boolean;
  major: boolean;
  minor: boolean;
  info: boolean;
}

export interface PageData {
  clubs: HgClubInfo[];
  games: Games;
  playersByClubId: { [key: string]: PlayerListElement[] };
  playerTournamentResults: ClubRecentTournPlayerResult[];
  clubOptionsById: { [key: string]: HgGetClubOptionsReply };
  clubMembers: PlayerListElement[];
  clubSeasonsById: { [key: string]: ClubSeason[] };
  clubSeasonStatsById: { [key: string]: ClubSeasonStats[] };
  clubRecentTournamentSummary: ClubRecentTournamentSummary[];
  loading: { [key: string]: boolean };
  uiLayout: UILayout;
  error: HomeGamesError;
}

export interface NavLink {
  active?: boolean;
  translation: {
    defaultMessage: string;
    id: string;
  };
  route?: RoutingKey;
  values?: { [key: string]: string };
}

export type InviteLink = 'JOIN_CLUB';

export enum ClubRoute {
  Announcements = 'announcements',
  Lobby = 'lobby',
  Results = 'results',
  Members = 'members',
  Settings = 'settings',
  Standings = 'standings'
}

export enum MemberRoute {
  List = 'list',
  Pending = 'pending',
  Blocked = 'blocked'
}

export enum UserClubRole {
  Member,
  Manager,
  Admin,
  ShadowFounder,
  Observer,
  Confirmation,
  Guest,
  Suspended
}

export enum ClubPlayerStatus {
  APPROVED = 'HG_MEMBERSHIP_STATUS_APPROVED',
  APPLIED = 'HG_MEMBERSHIP_STATUS_APPLIED',
  REJECTED = 'HG_MEMBERSHIP_STATUS_REJECTED',
  BLOCKED = 'HG_MEMBERSHIP_STATUS_BLOCKED',
  SUSPENDED = 'HG_MEMBERSHIP_STATUS_SUSPENDED',
  RESIGNED = 'HG_MEMBERSHIP_STATUS_RESIGNED',
  GUEST = 'HG_MEMBERSHIP_STATUS_GUEST',
  BANNED = 'HG_MEMBERSHIP_STATUS_BANNED'
}

export enum ClubPlayerRole {
  MEMBER = 'HG_MEMBER_ROLE_MEMBER',
  FOUNDER = 'HG_MEMBER_ROLE_FOUNDER',
  ADMIN = 'HG_MEMBER_ROLE_ADMIN',
  SHADOW_FOUNDER = 'HG_MEMBER_ROLE_SHADOW_FOUNDER',
  OBSERVER = 'HG_MEMBER_ROLE_OBSERVER',
  APPLIED = 'HG_MEMBER_ROLE_APPLIED',
  GUEST = 'HG_MEMBER_ROLE_GUEST',
  SUSPENDED = 'HG_MEMBER_ROLE_SUSPENDED'
}

export enum GameType {
  'Cash Game',
  'Tournament'
}

export enum TileType {
  Cash = 'Cash',
  Upcoming = 'Upcoming',
  Registered = 'Registered',
  Recent = 'Recent'
}
